import { createPinia } from 'pinia'
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import * as Sentry from "@sentry/vue";

// Firebase Analytics imports
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { firebaseConfig, firebaseConfigIOS, firebaseConfigWeb } from './firebase.config';
import { analytics } from './helpers/analytics.js';

import { IonicVue } from '@ionic/vue'

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css'

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css'
import '@ionic/vue/css/structure.css'
import '@ionic/vue/css/typography.css'

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css'
import '@ionic/vue/css/float-elements.css'
import '@ionic/vue/css/text-alignment.css'
import '@ionic/vue/css/text-transformation.css'
import '@ionic/vue/css/flex-utils.css'
import '@ionic/vue/css/display.css'

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import { AVPlugin } from "vue-audio-visual";
import 'shepherd.js/dist/css/shepherd.css';

/**
 * Ionic Dark Mode
 * -----------------------------------------------------
 * For more info, please see:
 * https://ionicframework.com/docs/theming/dark-mode
 */

/* @import '@ionic/vue/css/palettes/dark.always.css'; */
/* @import '@ionic/vue/css/palettes/dark.class.css'; */
/* import '@ionic/vue/css/palettes/dark.system.css' */

/* Theme variables */
import './theme/variables.css'

import CoreuiVue from '@coreui/vue-pro'
import CIcon from '@coreui/icons-vue'
import '@coreui/coreui-pro/dist/css/coreui.min.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import "bootstrap-icons/font/bootstrap-icons.css";
import 'bootstrap/dist/js/bootstrap.bundle.min.js'

import { plugin, defaultConfig } from "@formkit/vue";
import { createMultiStepPlugin } from '@formkit/addons'
import "@formkit/themes/genesis";
import '@formkit/addons/css/multistep'

import CountryFlag from 'vue-country-flag-next'

import DashboardView from '@/views/DashboardView.vue'
import ChatCategories from '@/views/ChatCategories.vue'
import NiceHeader from '@/components/NiceHeader.vue'
import NiceFooter from '@/components/NiceFooter.vue'
import SiteHeader from '@/components/SiteHeader.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import VocabEntry from '@/components/VocabEntry.vue'
import CurrentLanguage from '@/components/CurrentLanguage.vue'
import CurrentLanguageFlag from '@/components/CurrentLanguageFlag.vue'
import { AudioManager } from '@/helpers/audiomanager'
import { PopoverManager } from '@/helpers/popovermanager'
import { isIosApp, isAndroidApp } from '@/helpers/utils.js'
import 'vue3-carousel/dist/carousel.css'
import disableBackSwipe from './helpers/disableBackSwipe.js'

import { createI18n } from 'vue-i18n'

import l_en from "./locales/en.json"
import { isApp } from './helpers/utils.js'
import { AdMob } from '@capacitor-community/admob'

var languages = {
  en: l_en,
}

const lang = localStorage.getItem('language') || 'en'

const i18n = createI18n({
  locale: lang,
  fallbackLocale: "en",
  messages: languages,
  legacy: false
})

import { ar, az, bg, bs, ca, cs, da, de, el, en, es, fa, fi, fr, fy, he, hr, hu, id, it, ja, kk, ko, lt, lv, nb, nl, pl, pt, ro, ru, sk, sl, sr, sv, tet, tg, th, tr, uk, uz, vi, zh, is, mn } from '@formkit/i18n'

const i18nLocales = { ar, az, bg, bs, ca, cs, da, de, el, en, es, fa, fi, fr, fy, he, hr, hu, id, it, ja, kk, ko, lt, lv, nb, nl, pl, pt, ro, ru, sk, sl, sr, sv, tet, tg, th, tr, uk, uz, vi, zh, is, mn }

import { cilArrowCircleRight, cilKeyboard, cilLightbulb, cilReload } from '@coreui/icons-pro';
var icons = { cilArrowCircleRight, cilKeyboard, cilLightbulb, cilReload }

const t = i18n.global.t

const getConfig = () => {
	return {
		hardwareBackButton: false,
		swipeBackEnabled: false,
		animated: false
	}
}

import { LOG_LEVEL, Purchases } from "@revenuecat/purchases-capacitor"

const app = createApp(App)

// Initialize Firebase Analytics for all platforms
const initializeFirebaseAnalytics = async () => {
  if (isApp) {
    // Mobile platforms - Capacitor Firebase Analytics auto-initializes from config files
    try {
      console.log('Initializing Firebase Analytics for mobile...')
      
      // Enable analytics (it's enabled by default, but being explicit)
      await FirebaseAnalytics.setEnabled({ enabled: true });
      
      console.log('Mobile Firebase Analytics initialized successfully')
      
      // Log initial app_open event
      await FirebaseAnalytics.logEvent({
        name: 'app_open'
      });
    } catch (error) {
      console.error('Mobile Firebase Analytics initialization failed:', error)
    }
  } else {
    // Web platform - Firebase web SDK will be initialized in analytics.js
    console.log('Web Firebase Analytics will be initialized by analytics service')
    
    // Log initial app_open event via analytics service
    setTimeout(() => {
      analytics.logEvent('app_open', {
        platform: 'web'
      });
    }, 1000); // Wait for web analytics to initialize
  }
}

Sentry.init({
  app,
  dsn: "https://0728acb22a6794603f93ca10bfeff5c4@o4507658790371328.ingest.us.sentry.io/4507658793189376",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Initialize Firebase Analytics and Analytics Service
(async () => {
  await initializeFirebaseAnalytics();
  await analytics.initialize();
})();

if (isApp) {
	console.log('isApp is true, initializing AdMob and adding class')
	document.body.classList.add('is-in-app')
	AdMob.initialize({
		initializeForTesting: true
	})
	
	// Set up AdMob event listeners for analytics
	AdMob.addListener('bannerAdLoaded', (info) => {
		analytics.trackAdView('banner', info.adUnitId || 'unknown');
	});
	
	AdMob.addListener('interstitialAdLoaded', (info) => {
		analytics.trackAdView('interstitial', info.adUnitId || 'unknown');
	});
	
	AdMob.addListener('rewardedAdLoaded', (info) => {
		analytics.trackAdView('rewarded', info.adUnitId || 'unknown');
	});
	
	AdMob.addListener('rewardedAdUserEarnedReward', (reward) => {
		analytics.trackRewardedAdComplete(reward.type || 'coins');
	});
}
app.use(createPinia())
app.use(IonicVue, getConfig())
app.use(router)
app.use(CoreuiVue)
app.use(plugin, defaultConfig({ locales: i18nLocales, locale: lang, plugins: [createMultiStepPlugin()] }))
app.use(AVPlugin)
app.provide('audiomanager', new AudioManager())
app.provide('popovermanager', new PopoverManager())
app.provide('icons', icons)
app.component('CIcon', CIcon)
app.component('dashboard-view', DashboardView)
app.component('chat-categories', ChatCategories)
app.component('country-flag', CountryFlag)
app.component('nice-header', NiceHeader)
app.component('nice-footer', NiceFooter)
app.component('vocab-entry', VocabEntry)
app.component('site-header', SiteHeader)
app.component('site-footer', SiteFooter)
app.component('loading-overlay', Loading)
app.component('current-language', CurrentLanguage)
app.component('current-language-flag', CurrentLanguageFlag)
app.directive('disable-back-swipe', disableBackSwipe)
app.use(i18n)

app.provide('i18n', i18n)

router.isReady().then(() => {
  app.mount('#app')
	// configure variables for RevenueCat if running in iOS app
	if (isIosApp) {
		var revenuecatKey = "appl_gLbaKzDWzugwemFzYbEmMkdbyYg"
		const configure = async () => {
			await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }); // Enable to get debug logs
			await Purchases.configure({
				apiKey: revenuecatKey
			})
			Purchases.enableAdServicesAttributionTokenCollection();
			await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
				console.log('customerInfo listener updated', customerInfo)
			});
		}
		configure().then( () => { "RevenueCat configured" } )
	} else if (isAndroidApp) {
		var revenuecatKey = "goog_GCMyGbSKNCjsOdArhpsnTCWAUPR"
		console.log('setting up revenuecat with ', revenuecatKey)
		const configure = async () => {
			await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }); // Enable to get debug logs
			await Purchases.configure({
				apiKey: revenuecatKey
			})
			await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
				console.log('customerInfo listener updated', customerInfo)
			});
		}
		configure().then( () => { "revenuecat configured" } )
	}
})

